// Card 17, light characteristics (R548, ui-map-interface.md §17.3.17).
//
// A chart sets a light's character beside it: `Fl(2) 10s 15M` is two flashes
// every ten seconds, seen fifteen miles. Here the same string stands beside
// a sonar ring: the ping cadence of §5 and the reach of the ring in px, so a
// commander reads what an installation hears without hovering it. An Oracle
// that lends its array reads `Fl(2)`, because a shared array is the two
// flashes (R453).
//
// **The rings are the server's** (R112). `scene.ringsPx` is the listening
// circle set the empire frame hands over, and the hull rings are the reach
// the wire states per hull. The piece computes no range of its own.
//
// **Own and own-squad hulls only.** A range over a stranger's hull is a
// figure the server withheld (§5, R199).
//
// **The selected or the hovered hull only** (R1445). A string beside every
// hull ring was noise on a map with many hulls, so a hull ring carries its
// string only while the commander has that hull selected or the pointer is
// over it. The static rings keep their strings: they are few, and they do
// not move.

import { chart as S } from '../../../gen/strings';
import { PING_EVERY_SEC, type ChartStep } from '../../../gen/presentation';
import type { FrameInfo } from '../../layer';
import { CHART_INK, faceOf } from './shared';
import { isNamedFleet } from './beacon-read';
import type {
  ChartContext, ChartSinks, Piece,
} from '../context';

/** One string the piece writes, and where. */
interface Light {
  x: number;
  y: number;
  text: string;
  alpha: number;
  /** The chart text step: a ring's own or a hull's (R1796). */
  step: ChartStep;
}

export class LightCharsPiece implements Piece {
  readonly id = 'light_chars' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'static' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [
    'hello', 'state', 'sites', 'selection', 'hover', 'reading', 'zoomband', 'font',
  ] as const;

  private lights: Light[] = [];
  /** Where the flare stands: the commander's own hold. */
  private flare = { x: 0, y: 0 };

  rebuild(ctx: ChartContext): void {
    const t = ctx.cfg.pieces.light_chars;
    const fig = t.fig;
    const hist = ctx.reading === 'historical';
    const every = PING_EVERY_SEC;
    this.lights = [];
    this.flare.x = ctx.scene.home.x;
    this.flare.y = ctx.scene.home.y;
    for (const ring of ctx.scene.ringsPx) {
      if (ring.range <= 0) continue;
      const range = Math.round(ring.range);
      this.lights.push({
        x: ring.x + Math.cos(fig.ring_angle) * ring.range + fig.dx,
        y: ring.y + Math.sin(fig.ring_angle) * ring.range + fig.dy,
        text: hist ? S.light_historical({ every, range }) : S.light_modern({ every, range }),
        alpha: fig.alpha,
        step: t.text.main,
      });
    }
    const fleets = ctx.scene.fleets;
    if (!fleets) return;
    for (const f of fleets.chartFleets()) {
      if (f.sonarPx <= 0 || !isNamedFleet(ctx, f.id)) continue;
      if (!f.own && !f.squad) continue;
      const range = Math.round(f.sonarPx);
      const cls = ctx.state.ship(f.cls)?.name ?? '';
      let text: string;
      if (f.sharing) {
        text = hist
          ? S.light_share_historical({ every, range })
          : S.light_share_modern({ every, range });
      } else if (hist) {
        text = S.light_hull_historical({ cls, range });
      } else {
        text = S.light_modern({ every, range });
      }
      this.lights.push({
        x: f.x + Math.cos(fig.hull_angle) * f.sonarPx + fig.dx,
        y: f.y + Math.sin(fig.hull_angle) * f.sonarPx + fig.hull_dy,
        text,
        alpha: fig.hull_alpha,
        step: t.text.hull,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.light_chars;
    const fig = t.fig;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    // The chart's light mark over the hold.
    const fx = this.flare.x, fy = this.flare.y - fig.flare_h + fig.flare_lift;
    if (hist) {
      // The engraved tower's rays, from the mockup's own arc.
      const h = t.historical.fig;
      for (let k = 0; k < h.ray_count; k++) {
        const a = -Math.PI + (k * Math.PI) / 6;
        sinks.segments.line(
          fx + Math.cos(a) * h.ray_r0, fy + Math.sin(a) * h.ray_r0,
          fx + Math.cos(a) * h.ray_r1, fy + Math.sin(a) * h.ray_r1,
          r.width, CHART_INK.pale, fig.flare_alpha,
        );
      }
    } else {
      // The flare, as one capsule of the card's height. The batches carry
      // no filled path, so the leaf shape of the mockup is a rounded bar of
      // the same height and the same magenta.
      const half = fig.flare_h / 2;
      sinks.segments.line(
        fx, fy - half + fig.cap_inset, fx, fy + half - fig.cap_inset,
        half, CHART_INK.magenta, fig.flare_alpha,
      );
    }
    for (const l of this.lights) {
      ctx.glyphs.word(
        sinks.quads, l.text, l.x, l.y,
        face, l.step, CHART_INK.pale, l.alpha,
      );
    }
  }
}

export const lightChars = new LightCharsPiece();
